import React, { useEffect, useMemo, useState } from "react";

import Box from "veneer/Box";
import Button from "veneer/Button";
import Drawer from "veneer/Drawer";
import Stack from "veneer/Stack";
import styled from "veneer/styled";
import TextInput from "veneer/TextInput";
import { Colors } from "veneer/Theme";
import Typography from "veneer/Typography";

const CatalogItem = styled("div")({
  alignItems: "center",
  border: `1px solid ${Colors.gray300}`,
  borderRadius: 8,
  display: "flex",
  gap: 16,
  justifyContent: "space-between",
  padding: 16,
  '&[draggable="true"]': {
    cursor: "grab",
  },
  '&[draggable="true"]:active': {
    cursor: "grabbing",
  },
});

const CatalogItemPreview = styled(Box)({
  alignItems: "center",
  backgroundColor: Colors.gray100,
  border: `1px solid ${Colors.gray300}`,
  borderRadius: 4,
  display: "flex",
  flexShrink: 0,
  height: 48,
  justifyContent: "center",
  width: 64,
});

export interface WidgetCatalogItem<WidgetType extends string> {
  added: boolean;
  description: string;
  height: number;
  repeatable: boolean;
  title: string;
  type: WidgetType;
  width: number;
}

export interface WidgetCatalogCategory<WidgetType extends string> {
  title: string;
  widgets: ReadonlyArray<WidgetCatalogItem<WidgetType>>;
}

interface WidgetCatalogDrawerProps<WidgetType extends string> {
  categories: ReadonlyArray<WidgetCatalogCategory<WidgetType>>;
  onAddWidget: (type: WidgetType) => void;
  onClose: () => void;
  onWidgetDragEnd?: () => void;
  onWidgetDragStart?: (widget: WidgetCatalogItem<WidgetType>) => void;
  open: boolean;
}

export default function WidgetCatalogDrawer<WidgetType extends string>({
  categories,
  onAddWidget,
  onClose,
  onWidgetDragEnd,
  onWidgetDragStart,
  open,
}: WidgetCatalogDrawerProps<WidgetType>) {
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!open) {
      setSearch("");
    }
  }, [open]);

  const filteredCategories = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    if (!normalizedSearch) {
      return categories;
    }

    return categories
      .map((category) => ({
        ...category,
        widgets: category.widgets.filter(
          (widget) =>
            widget.title.toLowerCase().includes(normalizedSearch) ||
            widget.description.toLowerCase().includes(normalizedSearch) ||
            category.title.toLowerCase().includes(normalizedSearch),
        ),
      }))
      .filter((category) => category.widgets.length > 0);
  }, [categories, search]);

  return (
    <Drawer
      dataTestId={{ drawer: "dashboard-widget-catalog" }}
      onClose={onClose}
      open={open}
      size="small"
      title="Add widget"
      variant="persistent"
    >
      <Stack gap={3}>
        <TextInput
          autoFocus
          dataTestId="dashboard-widget-catalog-search"
          fullWidth
          label="Search widgets"
          onChange={(value) => setSearch(value ?? "")}
          placeholder="Search by name, category, or description"
          value={search}
        />
        {filteredCategories.length === 0 && (
          <Typography color={Colors.gray700} variant="body2">
            No widgets match your search.
          </Typography>
        )}
        {filteredCategories.map((category) => (
          <Stack gap={1} key={category.title}>
            <Typography variant="h5">{category.title}</Typography>
            {category.widgets.map((widget) => {
              const disabled = !widget.repeatable && widget.added;
              return (
                <CatalogItem
                  draggable={!disabled}
                  key={widget.type}
                  onDragEnd={onWidgetDragEnd}
                  onDragStart={(event) => {
                    event.dataTransfer.effectAllowed = "copy";
                    event.dataTransfer.setData("text/plain", widget.type);
                    onWidgetDragStart?.(widget);
                  }}
                >
                  <CatalogItemPreview>
                    <Typography color={Colors.gray700} variant="caption">
                      {widget.width} × {widget.height}
                    </Typography>
                  </CatalogItemPreview>
                  <Stack flex={1} gap={0.5} minWidth={0}>
                    <Typography variant="h6">{widget.title}</Typography>
                    <Typography color={Colors.gray700} variant="body2">
                      {widget.description}
                    </Typography>
                    <Typography color={Colors.gray700} variant="caption">
                      {widget.repeatable
                        ? "Can be added multiple times"
                        : "Single widget"}
                    </Typography>
                  </Stack>
                  <Button
                    disabled={disabled}
                    onClick={() => onAddWidget(widget.type)}
                    variant="outlined"
                  >
                    {!widget.repeatable && widget.added ? "Added" : "Add"}
                  </Button>
                </CatalogItem>
              );
            })}
          </Stack>
        ))}
      </Stack>
    </Drawer>
  );
}
